import { Badge, Button, Code, Icon, Link, Stack, Text, Tooltip } from "@chakra-ui/react"
import { createColumnHelper } from "@tanstack/react-table"
import { ConfirmationDialog } from "components/confirmation/confirmation"
import { ActionDrawer } from "components/drawer"
import { defaultColumns } from "components/table/defaultColumns"
import { removeDeviceRecord } from "lib/firebase"
import { deviceUpdatedTime } from "lib/devices"
import { formatBytes } from "lib/utils"
import { formatSeconds } from "lib/utils"
import moment from "moment"
import { MdClose, MdShareLocation } from "react-icons/md"
import BroadcastModal from "views/modal/broadcast"
import DeviceModal from "views/modal/device"
import LocationModal from "views/modal/location"

const helper = createColumnHelper()

export const columnsDevices = [
  helper.accessor('code', {
    size: 500,
    cell: props => (
      <Code fontSize='sm' fontWeight='700'>
        {props.getValue()}
      </Code>
    ),
  }),
  helper.accessor('location.name', {
    id: 'location',
    header: 'Location',
    size: 270,
    // The address of the location is not drawn here, but it always was searchable
    meta: { searchText: item => [item.location?.name, item.location?.address].filter(Boolean).join(' ') || 'None' },
    cell: props => (
      props.row.original.location ? (
        <ActionDrawer
          title='Update location'
          size='xl'
          trigger={
            <Stack direction='row' spacing={2} align='center'>
              <Icon h={4} w={4} as={MdShareLocation} color='brand.500'  />
              <Link color='brand.500'>{props.row.original.location.name}</Link>
            </Stack>
          }
          >
          <LocationModal location={props.row.original.location} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
      ) : (
        <Badge fontSize='0.7em'>None</Badge>
      )
    ),
  }),
  helper.accessor('model', {
    id: 'specs',
    header: 'Specs',
    size: 200,
    // The labels are part of what is shown, so "android 12" finds a device with Android 12
    meta: { searchText: item => `Model: ${item.model || 'N/A'} Android: ${item.androidVersion || 'N/A'}` },
    cell: props => {
      return <Stack direction='column' spacing={2}>
        <div><strong>Model:</strong> {props.row.original.model || 'N/A'}</div>
        <div><strong>Android:</strong> {props.row.original.androidVersion || 'N/A'}</div>
      </Stack>
    }
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 100,
    meta: { searchText: item => item.status || 'N/A' },
    cell: props => {
      const color = {
        'OK': 'green',
        'ERROR': 'red'
      }[props.row.original.status]
      return (
        <Tooltip label={props.row.original.message}>
          <Badge fontSize='0.7em' colorScheme={color}>{props.row.original.status || 'N/A'}</Badge>
        </Tooltip>
      );
    }
  }),
  helper.accessor('appVersion', {
    id: 'appVersion',
    header: 'Version',
    size: 100,
    meta: { searchText: item => item.appVersion || 'N/A' },
    cell: props => {
      return <Stack direction='column' spacing={2}>
        <div>{props.row.original.appVersion || 'N/A'}</div>
      </Stack>
    }
  }),
  // When the device was created or last saved in the admin app (not the player's pings). Sorted
  // by the real time; a device saved before `updated_at` was written shows when it was created.
  helper.accessor((row) => deviceUpdatedTime(row) ?? undefined, {
    id: 'updated',
    header: 'Updated',
    size: 200,
    meta: { searchText: item => { const time = deviceUpdatedTime(item); return time === null ? 'Unknown' : moment(time).format('LLL') } },
    cell: props => (
      <Text>
        {props.getValue() ? moment(props.getValue()).format('LLL') : 'Unknown'}
      </Text>
    ),
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    size: 200,
    cell: props => (
      <Stack direction='row' spacing={2} align='center'>
        <ActionDrawer
          title='Update device'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Update
            </Button>
          }
          >
          <DeviceModal device={props.row.original} onChange={props.table.options.context?.refresh}/>
        </ActionDrawer>
        <ConfirmationDialog
          title='Delete device'
          message='Are you sure you want to delete this device?'
          confirmLabel='Delete'
          trigger={
            <Button colorScheme='red' variant='ghost' size='xs'>
              Delete
            </Button>
          }
          onConfirm={async () => {
            await removeDeviceRecord(props.row.original)
            props.table.options.context?.refresh()
          }}
          />
      </Stack>
    ),
  }),
]